import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { InvoiceAutopay } from '../types/models/Wallet';
import type { ApiResponseInvoiceAutopay } from '../types/api/ApiResponseWallet';

export interface UseInvoiceAutopayReturn {
  /** The opt-in, its terms and its context, or `null` until loaded / on error. */
  autopay: InvoiceAutopay | null;
  /**
   * `false` once the backend answered HTTP 404 — the route isn't deployed (an unknown route is a plain
   * 404, not JSend), so the consumer hides the setting instead of showing an error.
   */
  available: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace the local state with a successful `PUT`'s returned object. */
  setAutopay: (next: InvoiceAutopay) => void;
}

/**
 * `GET /clients/invoice-autopay` — whether ALL the organization's outstanding invoices are paid
 * automatically, with the terms the user must accept to turn it on (docs/api-types/wallet.md).
 * Impersonation-safe read of the in-scope client; the `PUT` lives in the card that renders the switch.
 */
export const useInvoiceAutopay = (): UseInvoiceAutopayReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [autopay, setAutopay] = useState<InvoiceAutopay | null>(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAutopay = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/clients/invoice-autopay');
      if (response.status === 404) {
        setAvailable(false);
        return;
      }
      // JSend: branch on body.status, never response.ok (errors arrive at HTTP 200).
      const body = (await response.json().catch(() => null)) as ApiResponseInvoiceAutopay | null;
      if (body?.status === 'success' && body.data?.invoice_autopay) {
        setAvailable(true);
        setAutopay(body.data.invoice_autopay);
        return;
      }
      setError(body?.message ?? 'Could not load the automatic invoice payment setting.');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An error occurred while loading the automatic invoice payment setting.',
      );
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchAutopay();
    }
  }, [auth.isAuthenticated, fetchAutopay]);

  return { autopay, available, loading, error, refetch: fetchAutopay, setAutopay };
};

export default useInvoiceAutopay;
